import React, { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Shield,
    Ship,
    Pill,
    Flame,
    Mountain,
    Wheat,
    Building2,
    Banknote,
    Users,
    Link2,
    FileSignature,
    Handshake,
    Network,
    Globe2,
    ScrollText,
    Lock,
    ShieldCheck,
    Send,
    Loader2,
    Phone,
    Mail,
    MapPin,
    UploadCloud,
    X,
    ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURED_VERTICALS = [
    {
        key: "defence",
        icon: Shield,
        title: "Defence & Aerospace",
        desc: "Indravax supports defence, aerospace, homeland security, UAV, counter-UAS, surveillance, ammunition supply-chain, and dual-use technology opportunities through strategic advisory, representation, documentation, and structured transaction support.",
        focus: [
            "UAVs and drone systems",
            "Counter-UAS platforms",
            "Surveillance and ISR solutions",
            "Ammunition and defence supply chains",
            "Aerospace components",
            "Homeland security technologies",
            "Dual-use systems",
            "Defence procurement support",
        ],
        cta: "Explore Defence & Aerospace Vertical",
        href: "/sectors/defence-aerospace",
        image: "https://images.unsplash.com/photo-1559183731-4f9a33317d9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxmaWdodGVyJTIwamV0JTIwc2t5fGVufDB8fHx8MTc3NzAzNTIxMXww&ixlib=rb-4.1.0&q=85",
        testId: "sector-card-defence-featured",
        exploreTestId: "sector-defence-explore-btn",
    },
    {
        key: "commodities",
        icon: Ship,
        title: "Commodities & International Trade",
        desc: "Indravax supports structured commodity and international trade opportunities involving fertilizers, sulphur, agricultural commodities, metals, minerals, industrial raw materials, food products, leather products, bulk cargo, supplier-buyer validation, trade documentation, and cross-border transaction support.",
        focus: [
            "Urea, DAP, NPK and Potash",
            "Sulphur",
            "Agricultural commodities",
            "Pulses, legumes and food products",
            "Metals and minerals",
            "Industrial raw materials",
            "CIF / CFR / FOB structures",
            "LC-backed transaction support",
        ],
        cta: "Explore Commodities & International Trade",
        href: "/sectors/commodities-international-trade",
        image: "https://images.unsplash.com/photo-1605745341112-85968b19335b?auto=format&fit=crop&w=1800&q=80",
        testId: "sector-card-commodities-featured",
        exploreTestId: "sector-commodities-explore-btn",
    },
];

const DEFENCE_FEATURED = FEATURED_VERTICALS[0];

const SECTORS = [
    {
        key: "pharma",
        icon: Pill,
        title: "Pharmaceuticals & Healthcare",
        desc: "Public-health systems, institutional procurement, and healthcare infrastructure advisory across emerging and frontier markets.",
        tags: ["Public Health", "Infrastructure", "Policy"],
        image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1600&q=80",
    },
    {
        key: "energy",
        icon: Flame,
        title: "Oil, Gas & Energy",
        desc: "Energy markets, refined-product flows, LNG, and transaction structuring across producing and consuming jurisdictions.",
        tags: ["Crude", "LNG", "Refined Products"],
        image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80",
    },
    {
        key: "mining",
        icon: Mountain,
        title: "Mining, Metals & Minerals",
        desc: "Offtake structuring, concession advisory, and counterparty coordination across base, precious and battery metals.",
        tags: ["Offtake", "Concessions", "Logistics"],
        image: "https://images.unsplash.com/photo-1605004533616-1cd23e9bcacd?auto=format&fit=crop&w=1600&q=80",
    },
    {
        key: "agri",
        icon: Wheat,
        title: "Agri-Commodities & Fertilisers",
        desc: "Urea, NPK, sugar, edible oils and agri-inputs — advisory across producers, traders and end-buyers across corridors.",
        tags: ["Fertilisers", "Grains", "Specialty Agri"],
        image: "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=1600&q=80",
    },
    {
        key: "infra",
        icon: Building2,
        title: "Infrastructure & EPC",
        desc: "Strategic advisory on large-scale infrastructure, EPC mandates, and public-private project structuring.",
        tags: ["EPC", "PPP", "Project Finance"],
        image: "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1600&q=80",
    },
    {
        key: "finance",
        icon: Banknote,
        title: "Finance & Capital Markets",
        desc: "Trade finance structuring, capital introductions, and institutional coordination — SBLC, LC, BG and payment-security frameworks.",
        tags: ["SBLC", "LC", "BG"],
        image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1600&q=80",
    },
];

const ROLE_GRID = [
    { icon: Users, title: "Representation", desc: "Acting as institutional representative for clients across jurisdictions." },
    { icon: Link2, title: "Deal Structuring", desc: "Designing commercial, financial and operational frameworks." },
    { icon: FileSignature, title: "Negotiation Advisory", desc: "Documentation, contract architecture and counterparty alignment." },
    { icon: Handshake, title: "Counterparty Coordination", desc: "Bringing buyers, suppliers, partners and institutions to a single table." },
    { icon: Network, title: "Cross-Border Facilitation", desc: "Multi-jurisdiction execution across regulated environments." },
    { icon: Globe2, title: "Strategic Introductions", desc: "Selective access to principals, decision-makers and capital partners." },
];

const COMPLIANCE_POINTS = [
    "Engagement under clear, written mandate",
    "Counterparty KYC and provenance checks",
    "AML, sanctions and trade-control screening",
    "Activity within friendly jurisdictions only",
    "Documentation aligned with applicable export & finance laws",
];

const PROCESS_TIMELINE = [
    { n: "01", t: "Opportunity Intake", d: "Confidential review of the brief, sector and counterparty profile." },
    { n: "02", t: "Feasibility & Mandate", d: "Validation of scope, financial readiness and regulatory fit." },
    { n: "03", t: "Counterparty Alignment", d: "Coordination of buyers, suppliers, partners and institutions." },
    { n: "04", t: "Commercial Structuring", d: "Contract architecture, pricing framework and risk allocation." },
    { n: "05", t: "Financial Instruments", d: "LC, SBLC, BG, escrow and payment-security alignment." },
    { n: "06", t: "Execution & Closure", d: "Documentation, delivery coordination and post-closing support." },
];

const FAQS = [
    {
        q: "Does Indravax act as a buyer or seller?",
        a: "No. Indravax operates strictly as an advisory and facilitation firm. We do not take title to goods or act as principal unless specifically authorised under written agreement and applicable law.",
    },
    {
        q: "Which jurisdictions do you operate across?",
        a: "We engage across India, the GCC, parts of Africa, South-East Asia and Europe — strictly within friendly jurisdictions and subject to sanctions, export-control and AML screening.",
    },
    {
        q: "What is the typical engagement model?",
        a: "Engagements are structured under written mandate with defined scope, deliverables and confidentiality. We work on retained advisory or success-based facilitation depending on mandate type.",
    },
    {
        q: "How quickly do you respond to enquiries?",
        a: "Verified enquiries with clear scope, financial readiness and counterparty profile are typically reviewed by a principal within two working days.",
    },
];

const SECTOR_OPTIONS = [
    "Defence & Aerospace",
    "Commodities & International Trade",
    ...SECTORS.map((s) => s.title),
];
const COUNTERPARTY_OPTIONS = [
    "Buyer",
    "Seller / Supplier",
    "Intermediary / Mandate",
    "Government / Institutional",
    "Investor / Capital Partner",
    "Other",
];
const REGION_OPTIONS = [
    "India / South Asia",
    "GCC / Middle East",
    "Africa",
    "South-East Asia",
    "Europe",
    "Americas",
    "Global / Multi-Region",
];

const initialForm = {
    name: "",
    company: "",
    designation: "",
    country: "",
    email: "",
    phone: "",
    sector: SECTOR_OPTIONS[0],
    counterparty_type: COUNTERPARTY_OPTIONS[0],
    opportunity_details: "",
    scope: "",
    target_region: REGION_OPTIONS[0],
    documents_available: "",
    message: "",
};

const ALLOWED_EXTS = [".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg"];
const MAX_BYTES = 10 * 1024 * 1024;

const Sectors = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [file, setFile] = useState(null);
    const [openFaq, setOpenFaq] = useState(0);
    const fileInputRef = useRef(null);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
            toast.error("Unsupported file type. Allowed: PDF, DOC, DOCX, PNG, JPG.");
            e.target.value = "";
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error("File exceeds 10 MB limit.");
            e.target.value = "";
            return;
        }
        setFile(f);
    };

    const removeFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.sector.trim() || !form.opportunity_details.trim()) {
            toast.error("Name, email, sector and opportunity details are required.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            if (file) fd.append("file", file);
            await axios.post(`${API}/sector-enquiry`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Enquiry received. A principal will respond within two working days.");
            setForm(initialForm);
            removeFile();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Unable to submit at this time. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-enter">
            <SEO
                title="Sectors — Defence, Trade, Energy, Healthcare, Finance"
                description="Indravax Strategic Advisory operates across defence, international trade, pharmaceuticals, oil & gas, mining, agri-commodities, infrastructure and finance — with structured, compliance-driven execution."
                path="/sectors"
                keywords="sector advisory, defence advisory, trade and commodities advisory, oil gas energy advisory, mining advisory, pharmaceuticals healthcare advisory, infrastructure EPC advisory, trade finance SBLC LC BG, cross-border advisory"
            />

            {/* 1. HERO */}
            <section className="relative min-h-[82vh] flex items-center overflow-hidden" data-testid="sectors-hero">
                <img
                    src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2400&q=80"
                    alt="Global infrastructure and connected markets"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#040B1A]/85" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#040B1A] via-[#040B1A]/85 to-[#040B1A]/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <GlobeLines className="opacity-[0.12]" />
                <div className="grain-overlay" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <span className="h-px w-10 bg-[#C9A14A]" />
                            <span className="overline">Sectors</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.25rem] text-white leading-[1.0] tracking-tight">
                            Eight sectors.<br />
                            <span className="italic font-light text-[#C9A14A]">One execution standard.</span>
                        </h1>
                        <p className="mt-10 text-lg md:text-xl text-slate-300 font-light leading-[1.8] max-w-2xl">
                            Indravax operates across regulated, capital-intensive sectors where institutional advisory,
                            cross-border coordination and disciplined execution decide outcomes.
                        </p>
                        <p className="mt-3 text-[0.7rem] tracking-[0.3em] uppercase text-[#C9A14A]">
                            Precision · Compliance · Cross-Border Execution
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-5">
                            <a href="#submit" className="btn-primary group" data-testid="sectors-hero-submit">
                                Submit a Sector Requirement
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </a>
                            <Link to="/contact" className="btn-secondary" data-testid="sectors-hero-consult">
                                Request Consultation
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 2. OVERVIEW */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="sectors-overview">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-4">
                        <Overline>Overview</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Selectively engaged. <em className="text-[#C9A14A] not-italic">Structurally deep.</em>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-[1.9]">
                        <p>
                            Our sector practice is intentionally narrow. We engage where institutional advisory,
                            regulatory fluency and counterparty access translate into measurable outcomes —
                            <span className="text-[#C9A14A]"> not where activity is mistaken for execution.</span>
                        </p>
                        <p>
                            Across defence, trade, energy, healthcare, mining, agri-commodities, infrastructure and
                            finance, Indravax aligns suppliers, buyers, capital providers and institutions inside
                            a single, compliance-led execution architecture.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* 3. EIGHT SECTOR CARDS — Defence featured, then 7-card grid */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="sectors-grid">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Sector Coverage"
                        title="Eight focused verticals."
                        description="Each sector is led with structured advisory, compliance-first execution and access to qualified counterparties. Defence & Aerospace and Commodities & International Trade are our flagship verticals."
                        testId="sectors-grid-heading"
                    />

                    {/* FEATURED VERTICALS — Defence & Commodities */}
                    <div className="space-y-8 mb-10">
                        {FEATURED_VERTICALS.map((v, idx) => {
                            const VIcon = v.icon;
                            return (
                                <FadeUp key={v.key} delay={idx * 0.08}>
                                    <article
                                        className={`group relative overflow-hidden border-2 border-[#C9A14A]/50 bg-[#0A1F44]/60 grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
                                        data-testid={v.testId}
                                    >
                                        <div className="relative aspect-[16/9] lg:aspect-auto overflow-hidden lg:[direction:ltr]">
                                            <img
                                                src={v.image}
                                                alt={v.title}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-r from-[#040B1A]/30 via-transparent to-[#0A1F44]/80" />
                                            <div className="absolute inset-0 lg:bg-gradient-to-l lg:from-[#0A1F44]/90 lg:via-transparent lg:to-transparent" />
                                            <div className="absolute top-6 left-6 inline-flex items-center gap-3">
                                                <span className="bg-[#C9A14A] text-[#040B1A] px-3 py-1 text-[0.6rem] tracking-[0.3em] uppercase font-semibold">
                                                    Flagship Vertical
                                                </span>
                                            </div>
                                            <VIcon size={36} strokeWidth={1.1} className="absolute top-6 right-6 text-[#C9A14A]" />
                                        </div>
                                        <div className="p-8 md:p-12 flex flex-col justify-center lg:[direction:ltr]">
                                            <div className="overline mb-4">0{idx + 1} · {v.title}</div>
                                            <h3 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                                                {v.title}
                                            </h3>
                                            <p className="mt-5 text-base text-slate-300 font-light leading-[1.85]">
                                                {v.desc}
                                            </p>
                                            <div className="mt-6">
                                                <div className="text-[0.65rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3">
                                                    Focus Areas
                                                </div>
                                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                                                    {v.focus.map((f) => (
                                                        <li key={f} className="flex items-baseline gap-2 text-sm text-slate-300 font-light">
                                                            <span className="h-px w-3 bg-[#C9A14A] shrink-0 translate-y-2" />
                                                            <span>{f}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="mt-8">
                                                <Link
                                                    to={v.href}
                                                    className="btn-primary group/cta"
                                                    data-testid={v.exploreTestId}
                                                >
                                                    {v.cta}
                                                    <ArrowRight size={14} className="transition-transform group-hover/cta:translate-x-1" />
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                </FadeUp>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SECTORS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <FadeUp key={s.key} delay={i * 0.06}>
                                    <article
                                        className="group relative overflow-hidden border border-[#C9A14A]/20 bg-[#0A1F44]/40 h-full flex flex-col"
                                        data-testid={`sector-card-${s.key}`}
                                    >
                                        <div className="relative aspect-[16/9] overflow-hidden">
                                            <img
                                                src={s.image}
                                                alt={s.title}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-[#040B1A] via-[#040B1A]/50 to-transparent" />
                                            <div className="absolute top-5 left-5 overline">0{i + 3}</div>
                                            <Icon size={26} strokeWidth={1.1} className="absolute top-5 right-5 text-[#C9A14A]" />
                                        </div>
                                        <div className="p-7 md:p-8 flex-1 flex flex-col">
                                            <h3 className="font-serif text-2xl md:text-[1.6rem] text-white leading-tight">
                                                {s.title}
                                            </h3>
                                            <p className="mt-4 text-sm text-slate-400 font-light leading-relaxed flex-1">
                                                {s.desc}
                                            </p>
                                            <div className="mt-6 flex flex-wrap gap-2">
                                                {s.tags.map((t) => (
                                                    <span
                                                        key={t}
                                                        className="px-3 py-1 border border-[#C9A14A]/40 text-[0.65rem] tracking-[0.22em] uppercase text-[#C9A14A]"
                                                    >
                                                        {t}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </article>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 4. OUR ROLE */}
            <section className="py-24 md:py-32 relative overflow-hidden" data-testid="sectors-role">
                <GlobeLines className="opacity-[0.06]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        overline="Our Role"
                        title="What we actually do."
                        description="Across every sector, our role is execution-shaped: representation, structuring, negotiation and coordination — not introductions for their own sake."
                        testId="sectors-role-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {ROLE_GRID.map((r, i) => {
                            const Icon = r.icon;
                            return (
                                <FadeUp key={r.title} delay={i * 0.06}>
                                    <div className="institutional-card h-full" data-testid={`sectors-role-${i}`}>
                                        <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                        <h3 className="font-serif text-xl text-white mt-6 leading-tight">{r.title}</h3>
                                        <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{r.desc}</p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 5. COMPLIANCE BLOCK */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="sectors-compliance">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <Lock size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Compliance Architecture</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Compliance is the precondition, not the afterthought.
                            </h3>
                            <p className="mt-6 text-slate-300 font-light leading-[1.9]">
                                Indravax operates strictly within friendly jurisdictions, under written mandate,
                                and within applicable sanctions, export-control and anti-money-laundering frameworks.
                            </p>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <ShieldCheck size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Operating Standards</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Five disciplines we never trade away.
                            </h3>
                            <ul className="mt-8 space-y-4" data-testid="sectors-compliance-list">
                                {COMPLIANCE_POINTS.map((c) => (
                                    <li key={c} className="flex items-start gap-4 pb-4 border-b border-[#C9A14A]/10 last:border-0">
                                        <span className="h-px w-4 bg-[#C9A14A] mt-3 shrink-0" />
                                        <span className="text-slate-300 font-light">{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* 6. PROCESS TIMELINE */}
            <section className="py-24 md:py-32" data-testid="sectors-process">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Engagement Process"
                        title="A six-stage execution sequence."
                        description="Every sector mandate follows the same disciplined progression from intake to closure."
                        testId="sectors-process-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-[#C9A14A]/20">
                        {PROCESS_TIMELINE.map((s, i) => (
                            <FadeUp
                                key={s.n}
                                delay={i * 0.06}
                                className={`p-8 md:p-10 border-b border-[#C9A14A]/20 ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""} ${(i + 1) % 2 !== 0 ? "md:border-r lg:border-r" : ""}`}
                            >
                                <div className="font-serif text-5xl text-[#C9A14A] italic">{s.n}</div>
                                <h3 className="font-serif text-2xl text-white mt-6 leading-tight">{s.t}</h3>
                                <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{s.d}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7. FAQ */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="sectors-faq">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Frequently Asked"
                        title="Engagement, jurisdiction & response."
                        testId="sectors-faq-heading"
                    />
                    <div className="border-t border-[#C9A14A]/20">
                        {FAQS.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <FadeUp key={f.q} delay={i * 0.05}>
                                    <div className="border-b border-[#C9A14A]/15">
                                        <button
                                            type="button"
                                            onClick={() => setOpenFaq(open ? -1 : i)}
                                            className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                                            data-testid={`sectors-faq-q-${i}`}
                                        >
                                            <span className="font-serif text-lg md:text-xl text-white leading-snug group-hover:text-[#C9A14A] transition-colors">
                                                {f.q}
                                            </span>
                                            <ChevronDown
                                                size={20}
                                                className={`text-[#C9A14A] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                                            />
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 pr-10 text-slate-300 font-light leading-[1.9]" data-testid={`sectors-faq-a-${i}`}>
                                                {f.a}
                                            </p>
                                        </motion.div>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 8. SUBMIT A SECTOR REQUIREMENT FORM */}
            <section id="submit" className="py-24 md:py-32 scroll-mt-28" data-testid="sectors-submit">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        align="center"
                        overline="Initiate an Engagement"
                        title="Submit a sector requirement."
                        description="Every submission is reviewed by a principal under strict confidentiality. Verified enquiries receive a response within two working days."
                        testId="sectors-submit-heading"
                    />
                    <FadeUp>
                        <form
                            onSubmit={onSubmit}
                            encType="multipart/form-data"
                            className="institutional-card p-8 md:p-12"
                            data-testid="sectors-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Name *</label>
                                    <input type="text" name="name" value={form.name} onChange={onChange} className="institutional-input" placeholder="Full name" required data-testid="sectors-form-name" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Company</label>
                                    <input type="text" name="company" value={form.company} onChange={onChange} className="institutional-input" placeholder="Organisation" data-testid="sectors-form-company" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Designation</label>
                                    <input type="text" name="designation" value={form.designation} onChange={onChange} className="institutional-input" placeholder="e.g. Director, Principal" data-testid="sectors-form-designation" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Country</label>
                                    <input type="text" name="country" value={form.country} onChange={onChange} className="institutional-input" placeholder="Country of operation" data-testid="sectors-form-country" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Email *</label>
                                    <input type="email" name="email" value={form.email} onChange={onChange} className="institutional-input" placeholder="you@org.com" required data-testid="sectors-form-email" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Phone / WhatsApp</label>
                                    <input type="tel" name="phone" value={form.phone} onChange={onChange} className="institutional-input" placeholder="+..." data-testid="sectors-form-phone" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Sector *</label>
                                    <select name="sector" value={form.sector} onChange={onChange} className="institutional-input bg-[#040B1A]" required data-testid="sectors-form-sector">
                                        {SECTOR_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Counterparty Type</label>
                                    <select name="counterparty_type" value={form.counterparty_type} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="sectors-form-counterparty">
                                        {COUNTERPARTY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Scope / Quantity / Value</label>
                                    <input type="text" name="scope" value={form.scope} onChange={onChange} className="institutional-input" placeholder="e.g. 50,000 MT / USD 25M" data-testid="sectors-form-scope" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Target Region</label>
                                    <select name="target_region" value={form.target_region} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="sectors-form-region">
                                        {REGION_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Documents Available</label>
                                    <input type="text" name="documents_available" value={form.documents_available} onChange={onChange} className="institutional-input" placeholder="e.g. LOI, ICPO, FCO, SPA, POF, KYC pack" data-testid="sectors-form-docs" />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Opportunity Details *</label>
                                    <textarea
                                        name="opportunity_details"
                                        value={form.opportunity_details}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="Describe the requirement — sector context, counterparty profile, transaction structure and outcome sought."
                                        rows={5}
                                        required
                                        data-testid="sectors-form-details"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Additional Notes</label>
                                    <textarea name="message" value={form.message} onChange={onChange} className="institutional-input" placeholder="Timelines, jurisdictions, sensitivities." rows={3} data-testid="sectors-form-message" />
                                </div>

                                {/* FILE UPLOAD */}
                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Upload Document (optional)</label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        name="file"
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                                        onChange={onFileChange}
                                        className="hidden"
                                        data-testid="sectors-form-file-input"
                                    />
                                    {!file ? (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full border border-dashed border-[#C9A14A]/40 hover:border-[#C9A14A] bg-[#040B1A]/50 transition-colors p-8 flex flex-col items-center justify-center gap-3 group"
                                            data-testid="sectors-form-file-pick"
                                        >
                                            <UploadCloud size={28} className="text-[#C9A14A] group-hover:scale-110 transition-transform" />
                                            <span className="text-sm text-slate-300 font-light">
                                                Click to attach a document
                                            </span>
                                            <span className="text-[0.65rem] tracking-[0.25em] uppercase text-slate-500">
                                                PDF · DOC · DOCX · PNG · JPG · max 10 MB
                                            </span>
                                        </button>
                                    ) : (
                                        <div
                                            className="w-full border border-[#C9A14A]/40 bg-[#040B1A]/60 px-5 py-4 flex items-center justify-between gap-4"
                                            data-testid="sectors-form-file-selected"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <UploadCloud size={20} className="text-[#C9A14A] shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="text-sm text-white truncate">{file.name}</div>
                                                    <div className="text-[0.7rem] text-slate-500">
                                                        {(file.size / 1024).toFixed(1)} KB
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removeFile}
                                                className="text-slate-400 hover:text-[#C9A14A] transition-colors"
                                                data-testid="sectors-form-file-remove"
                                                aria-label="Remove file"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                                <p className="text-xs text-slate-500 max-w-md leading-relaxed font-light flex items-start gap-2">
                                    <ScrollText size={14} className="text-[#C9A14A] mt-0.5 shrink-0" />
                                    By submitting, you confirm the enquiry is made in good faith and in compliance
                                    with applicable laws and sanctions frameworks.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="sectors-form-submit"
                                >
                                    {submitting ? (
                                        <><Loader2 size={14} className="animate-spin" /> Submitting</>
                                    ) : (
                                        <>Submit Sector Requirement <Send size={14} /></>
                                    )}
                                </button>
                            </div>
                        </form>
                    </FadeUp>
                </div>
            </section>

            {/* 9. CONTACT + DISCLAIMER */}
            <section className="py-20 border-t border-[#C9A14A]/15 bg-[#07132E]/40" data-testid="sectors-contact">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div className="flex gap-3 text-sm text-slate-400">
                        <MapPin size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <span>Indravax Strategic Advisory LLP<br />New Delhi, India</span>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Phone size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="tel:+917814050036" className="hover:text-[#C9A14A] transition-colors">+91 78140 50036</a>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Mail size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="mailto:prateekgrewal@indravax.com" className="hover:text-[#C9A14A] transition-colors">prateekgrewal@indravax.com</a>
                    </div>
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
                    <p className="text-xs text-slate-500 font-light leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic">
                        Indravax Strategic Advisory LLP provides advisory and facilitation services across regulated
                        sectors. The firm does not act as a principal buyer, seller, broker or financial institution
                        unless specifically authorised under applicable laws and written agreements.
                    </p>
                </div>
            </section>

            {/* 10. FINAL CTA */}
            <section className="py-24 border-t border-[#C9A14A]/15" data-testid="sectors-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <SectionHeading
                        align="center"
                        overline="Engage"
                        title="Explore a sector-specific mandate."
                        testId="sectors-cta-heading"
                    />
                    <div className="flex flex-col sm:flex-row gap-5 justify-center">
                        <a href="#submit" className="btn-primary" data-testid="sectors-cta-btn">
                            Submit Requirement <ArrowRight size={14} />
                        </a>
                        <Link to="/contact" className="btn-secondary" data-testid="sectors-cta-contact">
                            Speak to a Principal
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Sectors;
